/**
 * Layout - Responsive Layout System
 * 
 * Handles layout calculations for the game.
 * 
 * Design principles:
 * - Always vertical orientation for gameplay consistency
 * - No forcing on vertical/square screens - use full space
 * - Only letterbox when screen is too wide (> 3:5 aspect ratio)
 * - All gameplay elements scale together relative to container
 */

import { CONTAINER_LAYOUT, CONVEYOR_LAYOUT, CONVEYOR_PHYSICS, INPUT, SCORING, REFERENCE_WIDTH, RENDERER, TIMING } from './Constants';

/** Maximum aspect ratio before letterboxing (width/height) */
const MAX_ASPECT_RATIO = 3 / 5;  // 0.6, a tall/portrait aspect ratio

/**
 * Complete layout dimensions for the game
 */
export interface LayoutDimensions {
    // Screen dimensions
    screenWidth: number;
    screenHeight: number;
    
    // Viewport (after letterboxing)
    viewportWidth: number;
    viewportHeight: number;
    viewportOffsetX: number;  // Left offset for centering letterboxed content
    
    // Container dimensions
    containerLeft: number;
    containerRight: number;
    containerTop: number;
    containerBottom: number;
    containerWidth: number;
    containerHeight: number;
    wallThickness: number;
    
    // Conveyor dimensions
    conveyorX: number;
    conveyorY: number;
    conveyorWidth: number;
    conveyorHeight: number;
    conveyorEndX: number;
    conveyorSpawnX: number;
    
    // Scale factor for packages (relative to reference width)
    scaleFactor: number;
    
    // Scaled values for other systems
    offScreenPadding: number;
    spawnBuffer: number;
    conveyorDetectionHeight: number;
    conveyorRestingThreshold: number;
    rotationMinTangentSpeed: number;
    rotationWarmupDistance: number;
    maxThrowSpeed: number;
    scoringGridCellSize: number;
    chevronSpacing: number;
    chevronSize: number;
}

/**
 * Layout calculator class
 */
export class Layout {
    private dimensions: LayoutDimensions | null = null;

    /**
     * Calculate layout for given screen dimensions.
     * 
     * @param screenWidth Actual screen/canvas width
     * @param screenHeight Actual screen/canvas height
     * @returns Calculated layout dimensions
     */
    calculate(screenWidth: number, screenHeight: number): LayoutDimensions {
        const screenAspect = screenWidth / screenHeight;
        
        let viewportWidth: number;
        let viewportHeight: number;
        let offsetX = 0;
        
        if (screenAspect > MAX_ASPECT_RATIO) {
            // Screen is too wide - letterbox sides
            viewportHeight = screenHeight;
            viewportWidth = screenHeight * MAX_ASPECT_RATIO;
            offsetX = (screenWidth - viewportWidth) / 2;
        } else {
            // Screen is vertical or acceptable aspect - use full width
            viewportWidth = screenWidth;
            viewportHeight = screenHeight;
        }
        
        // Calculate container dimensions relative to viewport
        const wallThickness = viewportWidth * CONTAINER_LAYOUT.wallThicknessPercent;
        const containerWidth = viewportWidth * CONTAINER_LAYOUT.widthPercent;
        const containerHeight = viewportHeight * CONTAINER_LAYOUT.heightPercent;
        const floorOffset = viewportHeight * CONTAINER_LAYOUT.floorOffsetPercent;
        
        // Container positioning (centered in viewport)
        const containerCenterX = offsetX + viewportWidth / 2;
        const containerLeft = containerCenterX - containerWidth / 2;
        const containerRight = containerCenterX + containerWidth / 2;
        const containerBottom = viewportHeight - floorOffset;
        const containerTop = containerBottom - containerHeight;
        
        // Scale factor for packages (relative to reference width)
        const scaleFactor = containerWidth / REFERENCE_WIDTH;
        
        // Calculate scaled values for other systems
        const offScreenPadding = viewportWidth * CONVEYOR_LAYOUT.offScreenPaddingPercent;
        const spawnBuffer = viewportHeight * TIMING.spawnBufferPercent;
        
        // Conveyor dimensions (now using percentage-based height)
        const conveyorHeight = Math.max(
            viewportHeight * CONVEYOR_LAYOUT.heightPercent,
            CONVEYOR_LAYOUT.minHeight
        );
        const conveyorY = viewportHeight * CONVEYOR_LAYOUT.yPercent;
        
        // Conveyor extends from off-screen left to 30% into container
        const conveyorX = offsetX - offScreenPadding;
        const dropZone = containerLeft + containerWidth * 0.30;
        const conveyorWidth = dropZone - conveyorX;
        const conveyorEndX = dropZone;
        const conveyorSpawnX = offsetX - offScreenPadding + (offScreenPadding * 0.33);
        
        // Conveyor physics scaled values
        const conveyorDetectionHeight = viewportHeight * CONVEYOR_PHYSICS.detectionHeightPercent;
        const conveyorRestingThreshold = viewportHeight * CONVEYOR_PHYSICS.restingThresholdPercent;
        
        // Input scaled values
        const rotationMinTangentSpeed = viewportWidth * INPUT.rotationMinTangentSpeedPercent;
        const rotationWarmupDistance = viewportWidth * INPUT.rotationWarmupDistancePercent;
        const maxThrowSpeed = viewportWidth * INPUT.maxThrowSpeedPercent;
        
        // Scoring scaled values
        const scoringGridCellSize = Math.max(containerWidth * SCORING.gridCellSizePercent, 10);
        
        // Renderer scaled values
        const chevronSpacing = Math.max(conveyorWidth * RENDERER.chevronSpacingPercent, 10);
        const chevronSize = Math.max(conveyorHeight * RENDERER.chevronSizePercent, 4);
        
        this.dimensions = {
            screenWidth,
            screenHeight,
            viewportWidth,
            viewportHeight,
            viewportOffsetX: offsetX,
            containerLeft,
            containerRight,
            containerTop,
            containerBottom,
            containerWidth,
            containerHeight,
            wallThickness,
            conveyorX,
            conveyorY,
            conveyorWidth,
            conveyorHeight,
            conveyorEndX,
            conveyorSpawnX,
            scaleFactor,
            offScreenPadding,
            spawnBuffer,
            conveyorDetectionHeight,
            conveyorRestingThreshold,
            rotationMinTangentSpeed,
            rotationWarmupDistance,
            maxThrowSpeed,
            scoringGridCellSize,
            chevronSpacing,
            chevronSize,
        };
        
        return this.dimensions;
    }
    
    /**
     * Get current layout dimensions.
     * Throws if calculate() hasn't been called.
     */
    getDimensions(): LayoutDimensions {
        if (!this.dimensions) {
            throw new Error('Layout.calculate() must be called first');
        }
        return this.dimensions;
    }
    
    /**
     * Check if layout has been calculated.
     */
    isReady(): boolean {
        return this.dimensions !== null;
    }
    
    /**
     * Get letterbox regions for rendering (returns areas to fill with black).
     * Returns empty array if no letterboxing is needed.
     */
    getLetterboxRegions(): Array<{ x: number; y: number; width: number; height: number }> {
        if (!this.dimensions) return [];
        
        const { screenHeight, viewportWidth, viewportOffsetX } = this.dimensions;
        
        if (viewportOffsetX <= 0) {
            // No letterboxing needed
            return [];
        }
        
        // Left and right letterbox bars
        return [
            { x: 0, y: 0, width: viewportOffsetX, height: screenHeight },
            { x: viewportOffsetX + viewportWidth, y: 0, width: viewportOffsetX, height: screenHeight },
        ];
    }
    
    /**
     * Convert screen coordinates to viewport coordinates.
     * Useful for input handling when letterboxing is active.
     */
    screenToViewport(screenX: number, screenY: number): { x: number; y: number; inViewport: boolean } {
        if (!this.dimensions) {
            return { x: screenX, y: screenY, inViewport: true };
        }
        
        const { viewportOffsetX, viewportWidth, viewportHeight } = this.dimensions;
        
        const x = screenX - viewportOffsetX;
        const y = screenY;
        
        const inViewport = x >= 0 && x <= viewportWidth && y >= 0 && y <= viewportHeight;
        
        return { x, y, inViewport };
    }
    
    /**
     * Get spawn position for new packages on conveyor.
     * 
     * @param packageHeight Height of package being spawned
     * @returns Position above conveyor, off-screen left
     */
    getSpawnPosition(packageHeight: number): { x: number; y: number } {
        if (!this.dimensions) {
            return { x: 0, y: 0 };
        }
        
        return {
            x: this.dimensions.conveyorSpawnX,
            y: this.dimensions.conveyorY - packageHeight / 2 - this.dimensions.spawnBuffer,
        };
    }
    
    /**
     * Check if a position is within the container bounds.
     */
    isInContainer(x: number, y: number): boolean {
        if (!this.dimensions) return false;
        
        const { containerLeft, containerRight, containerTop, containerBottom } = this.dimensions;
        
        return x > containerLeft && x < containerRight && y > containerTop && y < containerBottom;
    }
}

/** Singleton layout instance */
let layoutInstance: Layout | null = null;

/**
 * Get the global layout instance.
 */
export function getLayout(): Layout {
    if (!layoutInstance) {
        layoutInstance = new Layout();
    }
    return layoutInstance;
}
